import { employerActions } from 'redux/employer/employerSlice'
import Checkbox from 'core-system/Checkbox'
import FlexContainer from 'core-system/FlexContainer'
import Loading from 'core-system/Loading'
import { ProgramModal } from 'core-system/Program'
import Table, { TableRow, TableRowCell } from 'core-system/Table'
import { ServerPagination } from 'core-system/Table/Table'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import SegmentService from 'redux/config/services/SegmentService'
import { AppState } from 'redux/config/store'
import { Commuter } from 'redux/employee/employeeTypes'
import { Worksite } from 'redux/employer/employerTypes'
import RandomUtils from 'shared/RandomUtils'
import styled from 'styled-components'
import { employeeActions } from 'redux/employee/employeeSlice'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import AlternateButton from 'core-system/Button/AlternateButton'
import Avatar from 'core-system/Avatar/Avatar'
const Body = styled.div`
  margin: 1.5rem 0 1.5rem;
`

const UserContainer = styled.div`
  display: flex;
`

const NameContainer = styled.div`
  margin-left: 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

const getTableRows = (
  filteredCommuters: Commuter[],
  sortByFilter: string,
  selectedCommuters: string[],
  worksites: Worksite[],
  handleCommuterSelect: (
    e: React.MouseEvent<HTMLDivElement>,
    id: string,
    selectedCommuters: string[]
  ) => void
) => {
  const ordered =
    sortByFilter === 'server'
      ? filteredCommuters
      : [...filteredCommuters].sort((a, b) =>
          sortByFilter === 'desc'
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name)
        )
  return ordered.map((pendingCommuter, idx) => {
      const relevantWorksite = worksites.find(
        (wrk) => wrk.id === pendingCommuter.worksiteId
      )
      const worksiteName = relevantWorksite ? relevantWorksite.alias : 'unknown'

      return (
        <TableRow key={idx}>
          <TableRowCell>
            <Checkbox
              checked={selectedCommuters.includes(pendingCommuter.id)}
              onClick={(e) =>
                handleCommuterSelect(e, pendingCommuter.id, selectedCommuters)
              }
              marginRight='2%'
              data-cy='segments-pending-employees-checkbox'
            />
          </TableRowCell>
          <TableRowCell>
            <UserContainer>
              <Avatar name={pendingCommuter.name} size='medium' />
              <NameContainer>
                <Text variant='action4' hideOverflow>
                  {pendingCommuter.name}
                </Text>
                <Text variant='body2' textColor={palette.text.placeholder}>
                  {pendingCommuter.employeeCorporateId
                    ? `#${pendingCommuter.employeeCorporateId}`
                    : ''}
                </Text>
              </NameContainer>
            </UserContainer>
          </TableRowCell>
          <TableRowCell>{worksiteName}</TableRowCell>
          <TableRowCell>{pendingCommuter.email}</TableRowCell>
        </TableRow>
      )
    })
}

interface MyCommutersPending {
  activeSegment: string
  pendingCommuters: Commuter[]
  serverPagination?: ServerPagination
  isLoading?: boolean
}

const MyCommutersPending = React.memo((props: MyCommutersPending) => {
  const { activeSegment, pendingCommuters, serverPagination, isLoading } = props

  const dispatch = useDispatch()

  const {
    segmentUpdateFlag,
    worksites,
    employeesDeclinedFlag,
    employeesApprovedFlag,
    allEmployeeSegmentId,
  } = useSelector((state: AppState) => state.employer)

  const [selectedCommuters, setSelectedCommuters] = useState([])
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false)
  const [filteredCommuters, setFilteredCommuters] = useState(pendingCommuters)
  const [sortByFilter] = useState(serverPagination ? 'server' : 'desc')

  useEffect(() => {
    if (employeesDeclinedFlag) {
      dispatch(employerActions.toggleDeclineInactiveEmployeesFlag())
      setIsDeclineModalOpen(false)
    }
  }, [dispatch, employeesDeclinedFlag, allEmployeeSegmentId])

  useEffect(() => {
    if (employeesApprovedFlag) {
      if (serverPagination) {
        dispatch(employeeActions.refreshActiveRoster())
      } else {
        dispatch(
          employeeActions.getMyCommuters({ segment: allEmployeeSegmentId })
        )
        dispatch(employeeActions.getCommuters())
      }
      dispatch(employerActions.getSegments())
      dispatch(employerActions.toggleForceActivateInactiveEmployeesFlag())
    }
  }, [dispatch, employeesApprovedFlag, allEmployeeSegmentId, serverPagination])

  useLayoutEffect(() => {
    setFilteredCommuters(pendingCommuters)
    setSelectedCommuters([])
  }, [pendingCommuters])

  const handleCancel = () => {
    setSelectedCommuters([])
    SegmentService.track('commuters-group-manage', {
      action: 'cancel',
      numEmployees: null,
    })
  }

  useEffect(() => {
    if (segmentUpdateFlag) {
      handleCancel()
      dispatch(employerActions.toggleSegmentUpdateFlag())
    }
  }, [dispatch, handleCancel, segmentUpdateFlag])

  const selectAll = useCallback(
    (filteredCommuters: Commuter[], selectedCommuters: string[]) => {
      if (selectedCommuters.length === filteredCommuters.length) {
        setSelectedCommuters([])
      } else {
        const ids = filteredCommuters.map((commuters) => commuters.id)
        setSelectedCommuters(ids)
      }
      SegmentService.track('commuters-group-manage', {
        action: 'select-all',
        numEmployees: null,
      })
    },
    []
  )

  const handleCommuterSelect = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement>,
      id: string,
      selectedCommuters: string[]
    ) => {
      e.stopPropagation()
      if (selectedCommuters.includes(id)) {
        const commuterCopy = [...selectedCommuters]
        const idx = commuterCopy.indexOf(id)
        commuterCopy.splice(idx, 1)
        setSelectedCommuters(commuterCopy)
      } else {
        setSelectedCommuters((prev) => [...prev, id])
      }
    },
    []
  )

  const handleApprove = (
    selectedCommuters: string[],
    activeSegment: string
  ) => {
    dispatch(
      employerActions.forceActivateInactiveEmployees({
        segmentId: activeSegment,
        employees: selectedCommuters,
      })
    )
    SegmentService.track('commuters-group-manage', {
      action: 'activate',
      numEmployees: selectedCommuters.length,
    })
  }

  const handleDecline = (selectedCommuters: string[]) => {
    dispatch(
      employerActions.declineInactiveEmployees({
        segmentId: activeSegment,
        employees: selectedCommuters,
      })
    )
    SegmentService.track('commuters-group-manage', {
      action: 'decline',
      numEmployees: selectedCommuters.length,
    })
  }

  if (!pendingCommuters || !filteredCommuters || employeesDeclinedFlag) {
    return <Loading isCard={false} />
  }

  return (
    <>
      <Body>
        <Table
          maxRows={5}
          headerCells={[
            <TableRowCell>
              {filteredCommuters.length > 0 && (
                <Checkbox
                  checked={
                    filteredCommuters.length === selectedCommuters.length
                  }
                  onClick={() =>
                    selectAll(filteredCommuters, selectedCommuters)
                  }
                  marginRight='2%'
                  disabled={filteredCommuters.length === 0}
                  data-cy='select-all-commuters-checkbox'
                />
              )}
            </TableRowCell>,
            <TableRowCell>Commuter</TableRowCell>,
            <TableRowCell>Worksite</TableRowCell>,
            <TableRowCell>Email</TableRowCell>,
          ]}
          emptyMessage='No Results Found'
          isLoading={isLoading}
          serverPagination={serverPagination}
          rows={
            filteredCommuters.length > 0
              ? getTableRows(
                  filteredCommuters,
                  sortByFilter,
                  selectedCommuters,
                  worksites,
                  handleCommuterSelect
                )
              : null
          }
          resetPaginationOnRowsChange={false}
        />
      </Body>
      <Footer>
        <FlexContainer
          alignItems='center'
          justifyContent='space-between'
          width='100%'
        >
          <Text variant='action2'>{selectedCommuters.length} Selected</Text>
          <FlexContainer>
            <AlternateButton
              style={{ marginRight: '1rem' }}
              onClick={handleCancel}
              disabled={selectedCommuters.length === 0}
            >
              Cancel
            </AlternateButton>
            <SecondaryButton
              disabled={selectedCommuters.length === 0}
              onClick={() => setIsDeclineModalOpen(true)}
              style={{ marginRight: '1rem' }}
            >
              Decline
            </SecondaryButton>
            <PrimaryButton
              disabled={selectedCommuters.length === 0}
              onClick={() => handleApprove(selectedCommuters, activeSegment)}
            >
              Approve
            </PrimaryButton>
          </FlexContainer>
        </FlexContainer>
      </Footer>
      <ProgramModal
        title='Decline Commuters'
        description={`Are you sure you want to decline ${
          selectedCommuters.length
        } commuter${RandomUtils.pluralCheck(
          selectedCommuters.length
        )}? This will remove them from the platform!`}
        buttonVariant='cancel'
        buttonText='Decline'
        open={isDeclineModalOpen}
        closeModal={() => setIsDeclineModalOpen(false)}
        buttonCallback={() => handleDecline(selectedCommuters)}
      />
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  MyCommutersPending.displayName = 'MyCommutersPending'
}

export default MyCommutersPending
